/*
 * Pure helpers for the chat page (routes/chat.py's answers): merging what a
 * poll brings into what is shown, the reactions' pictures, a system line's
 * words, and times. Tested in messages.test.js.
 */

/** The server's reaction codes, in its order, and how each is drawn. */
export const REACTION_EMOJI = {
  thumbs_up: '👍', heart: '❤️', laugh: '😂', wow: '😮', mushroom: '🍄', microscope: '🔬',
};
export const REACTIONS = Object.keys(REACTION_EMOJI);

/** `incoming` folded into `shown`: one entry per message_id (the newer
 *  answer wins: an edit, a reaction, a removal), in message order. */
export function mergeMessages(shown, incoming) {
  const byId = new Map((shown || []).map((m) => [m.message_id, m]));
  for (const message of incoming || []) if (message && message.message_id) byId.set(message.message_id, message);
  return [...byId.values()].sort((a, b) => a.message_id - b.message_id);
}

/** The highest message number shown (the cursor), or 0. */
export function latestId(messages) {
  return (messages || []).reduce((top, m) => Math.max(top, m.message_id || 0), 0);
}

/** Unsent outbox entries the server already has (by client_key) drop out. */
export function stillUnsent(outbox, messages) {
  const sent = new Set((messages || []).map((m) => m.client_key).filter(Boolean));
  return (outbox || []).filter((e) => !sent.has(e.client_key));
}

/** A system line's words ("Ada added Bo and Cy"), from its event. */
export function systemText(message, t) {
  const s = message.system || {};
  const someone = t('someone');
  const actor = s.actor_name || someone;
  const users = (s.user_names || []).map((n) => n || someone).join(', ');
  switch (s.event) {
    case 'created': return users ? t('system.createdWith', { actor, users }) : t('system.created', { actor });
    case 'renamed': return t('system.renamed', { actor, title: s.title || '' });
    case 'added': return t('system.added', { actor, users });
    case 'left': return t('system.left', { user: s.user_name || actor });
    case 'joined': return t('system.joined', { user: s.user_name || someone });
    case 'created_event': return t('system.createdEvent', { actor });
    case 'removed': return t('system.removed', { actor, user: s.user_name || someone });
    default: return t('system.other');
  }
}

/** Whether a message names @username (its owner hears of it in "mentions" mode). */
export function mentions(body, username) {
  if (!body || !username) return false;
  return body.toLowerCase().includes(`@${username.toLowerCase()}`);
}

/** "14:32", or the day and time when it was not today. */
export function shortTime(iso, lang = 'en', now = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const locale = lang === 'he' ? 'he-IL' : 'en-GB';
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Whether my own message may still be edited (the server allows 15 minutes). */
export function editable(message, me, now = Date.now(), minutes = 15) {
  if (!message || message.removed || message.kind !== 'text' || message.sender_id !== me) return false;
  const at = new Date(message.created_at).getTime();
  return Number.isFinite(at) && now - at < minutes * 60 * 1000;
}
